import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { Card, Statistic } from "antd";
import React from "react";
import { useSearchContext } from "../../contexts/searchcontext.ts";
import { AffiliateLink } from "../../AffiliateLink";
import { EbayLogo } from "../../EbayLogo";
import { cardStatsInfoText, sealedWaxStatsInfoText, summaryFields } from "../../layouts/cardStatsSummary/assets";
import { usePopularChartContext } from "../../contexts/popularchartcontext";
import { ENTITY_TYPES } from "utils/constants";
import Helpers from "services/helpers";

const valueStyle = {};
const statStyle = {
  display: "flex",
  flexDirection: "column-reverse",
};

const PopularChartSummary = ({ entityType = ENTITY_TYPES.CARD }) => {
  const { selectedGraph, hiddenLines, cards, sealedWaxes } = usePopularChartContext();
  const { groupedPopularCardStats, popularCardStats, popularSealedWaxStats, groupedPopularSealedWaxStats } =
    useSearchContext();

  const [entities, grouped, stats] = {
    [ENTITY_TYPES.CARD]: [cards, groupedPopularCardStats, popularCardStats],
    [ENTITY_TYPES.SEALED_WAX]: [sealedWaxes, groupedPopularSealedWaxStats, popularSealedWaxStats],
  }[entityType];
  const filteredStats = Object.keys(stats).filter((entityId) => !hiddenLines[entityId]);
  const filteredEntities = entities.filter((entity) => !hiddenLines[entity.id]);

  if (Object.keys(stats).length === 0 && grouped.count === 0) {
    return null;
  }

  if (filteredStats.length === 1) {
    const totalStats = stats[filteredStats[0]];
    const isIncrease = totalStats.changeValue >= 0;
    return (
      <div style={{ padding: "10px" }}>
        <div className={"card-column-stats"}>
          <div style={{ flex: "1 0 33.33%" }}>
            <Card bordered={false} style={{ height: "100%" }}>
              <Statistic title={"Total # Sold"} value={totalStats.count} valueStyle={valueStyle} style={statStyle} />
              <Statistic
                title={"Total $ Sold"}
                value={totalStats.total}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
              <Statistic
                title={"Lowest Sale"}
                value={totalStats.low}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
              <Statistic
                title={"Highest Sale"}
                value={totalStats.high}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
            </Card>
          </div>
          <div style={{ flex: "0 1 33.33%" }}>
            <Card bordered={false} style={{ height: "100%" }}>
              <Statistic
                title={"Most Recent Sale Avg" + (totalStats.endAvg > 0 ? " (" + totalStats.endCount + " sales)" : "")}
                value={totalStats.endAvg}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
              <Statistic
                title={
                  "Last 14 Day Sale Avg" + (totalStats.last14Avg > 0 ? " (" + totalStats.last14Count + " sales)" : "")
                }
                value={totalStats.last14Avg ? totalStats.last14Avg : 0}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
              <Statistic
                title={"Avg Sale over Period"}
                value={totalStats.avg}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
              <Statistic
                title={
                  "Avg Sale at Start of Period" +
                  (totalStats.startAvg > 0 ? " (" + totalStats.startCount + " sales)" : "")
                }
                value={totalStats.startAvg}
                valueStyle={valueStyle}
                style={statStyle}
                precision={2}
                prefix={"$"}
              />
            </Card>
          </div>
          <div style={{ flex: "0 1 33.33%" }}>
            <Card bordered={false} style={{ height: "100%" }}>
              <Statistic
                title={"Change $"}
                value={Helpers.formatMoney(totalStats.changeValue)}
                valueStyle={{ color: isIncrease ? "green" : "red", fontWeight: 700, fontSize: "30px" }}
                style={statStyle}
                precision={2}
                prefix={<span>{isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />}</span>}
              />
              <Statistic
                title={"Change %"}
                value={totalStats.changePercent}
                valueStyle={{ color: isIncrease ? "green" : "red" }}
                style={statStyle}
                precision={1}
                suffix={"%"}
                prefix={isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              />

              {!!totalStats.query && (
                <div style={{ marginTop: 20 }}>
                  <AffiliateLink query={totalStats.query} expanded />
                </div>
              )}

              {filteredEntities.length > 0 ? <EbayLogo query={filteredEntities[0].query} /> : null}
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#ECECEC", padding: "10px" }}>
      <div className={"card-column-stats"}>
        <div style={{ flex: "1 0 50%" }}>
          <Card style={{ height: "100%" }}>
            {summaryFields[selectedGraph].map((field) => (
              <Statistic
                key={field.id}
                title={field.label}
                value={grouped[field.id]}
                precision={field.precision}
                prefix={field.prefix}
                suffix={field.suffix}
                valueStyle={valueStyle}
                style={statStyle}
              />
            ))}
          </Card>
        </div>
        <div style={{ flex: "0 1 50%" }}>
          <Card style={{ height: "100%" }}>
            <p style={{ fontSize: "18px" }}>
              {
                {
                  [ENTITY_TYPES.CARD]: cardStatsInfoText,
                  [ENTITY_TYPES.SEALED_WAX]: sealedWaxStatsInfoText,
                }[entityType]
              }
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PopularChartSummary;
